import axios from "axios";
import axiosRetry from "axios-retry";
import { config } from "./config";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: config.api.baseUrl,
  timeout: 60000, // Increased to 60 seconds for document downloads and heavy operations
  headers: {
    "Content-Type": "application/json",
  },
});

// Configure axios retry with exponential backoff
axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry on network errors, timeouts, and 5xx server errors
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      error.code === "ECONNABORTED" ||
      Boolean(error.response && error.response.status >= 500) ||
      error.response?.status === 429 // Add 429 to retry conditions
    );
  },
});

// Log the API URL for debugging (only in development)
if (config.app.environment === "development") {
  console.log("API Base URL:", apiClient.defaults.baseURL);
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only handle errors that have a response (not network errors)
    if (!error.response) {
      // Network error (connection refused, timeout, etc.)
      console.error("Network error:", error.message);
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      // Unauthorized - clear token and redirect to login
      // Only redirect if on a protected route (dashboard pages)
      const currentPath = window.location.pathname;
      const isAuthPage =
        currentPath.startsWith("/auth/login") ||
        currentPath.startsWith("/auth/register") ||
        currentPath.startsWith("/auth/forgot-password") ||
        currentPath.startsWith("/auth/reset-password") ||
        currentPath.startsWith("/auth/activate-account") ||
        currentPath.startsWith("/auth/otp-verification");
      
      const isProtectedRoute = currentPath.startsWith("/dashboard");

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      // Only redirect to login if user is on a protected route (not public pages)
      if (!isAuthPage && isProtectedRoute) {
        console.warn("⚠️ Unauthorized request on protected route. Redirecting to login...");
        window.location.href = "/auth/login";
      }
    }

    if (error.response.status === 403) {
      // Forbidden - show access denied message
      console.error("Access denied");
    }

    if (error.response.status >= 500) {
      // Server error - show generic error message
      console.error("Server error occurred");
    }

    return Promise.reject(error);
  }
);

export default apiClient;
